// localStorage + sync queue for SkinMemory AI
const KEYS = {
  USER_ID: "sm_user_id",
  AUTHED_USER_ID: "sm_authed_user_id",
  ONBOARDING_COMPLETE: "sm_onboarding_complete",
  SKIN_TYPE: "sm_skin_type",
  SCAN_COUNT: "sm_scan_count",
  LAST_CHECKIN: "sm_last_checkin_date",
  PRODUCTS: "sm_products",       // list of product records displayed on home/journal
  REACTIONS: "sm_reactions",
  SAFE_LIST: "sm_safe_list",
  DANGER_LIST: "sm_danger_list",
  // offline-first replay queues
  SYNC_USER_PENDING: "sm_sync_user_pending",
  SYNC_SCANS_PENDING: "sm_sync_scans_pending",
  SYNC_REACTIONS_PENDING: "sm_sync_reactions_pending",
  // cache of OBF lookups keyed by barcode
  PRODUCT_CACHE: "sm_product_cache",
  // server-synced state (source of truth for unlock conditions)
  SERVER_REACTION_COUNT: "sm_server_reaction_count",
  SUSPECT_CACHE: "sm_suspect_cache",
  // Pro state cache — always refreshed from server on app load
  IS_PRO: "sm_is_pro",
  // Auth — email persisted for account-switch detection
  EMAIL: "sm_email",
  FIRST_NAME: "sm_first_name",
  LAST_NAME: "sm_last_name",
  MANUAL_ENTRY_TOOLTIP_SHOWN: "sm_manual_tooltip_shown",
};

const safeGet = (key, fallback) => {
  try {
    const v = localStorage.getItem(key);
    return v === null ? fallback : JSON.parse(v);
  } catch {
    return fallback;
  }
};
const safeSet = (key, value) => {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* quota */ }
};

function genUuid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export const storage = {
  /* identity */
  getOrCreateUserId: () => {
    let id = safeGet(KEYS.USER_ID, null);
    if (!id) {
      id = genUuid();
      safeSet(KEYS.USER_ID, id);
      // Mark user creation as pending sync
      safeSet(KEYS.SYNC_USER_PENDING, true);
    }
    return id;
  },
  markUserSynced: () => safeSet(KEYS.SYNC_USER_PENDING, false),
  isUserPending: () => safeGet(KEYS.SYNC_USER_PENDING, true) === true,
  setUserId: (id) => {
    safeSet(KEYS.USER_ID, id);
    safeSet(KEYS.SYNC_USER_PENDING, false);
  },

  /* Authenticated user id — set when /api/auth/{login,register,me}
   * returns a user. This is the source of truth for identity while the
   * cookie session is valid; a stale sm_user_id (anon UUID) is never
   * used for reads/writes when this is populated. */
  getAuthedUserId: () => safeGet(KEYS.AUTHED_USER_ID, null),
  setAuthedUserId: (id) => {
    if (id) {
      safeSet(KEYS.AUTHED_USER_ID, id);
      // Also mirror to USER_ID so any legacy caller that still uses
      // getOrCreateUserId() lands on the authed row rather than a
      // freshly-minted anonymous UUID.
      safeSet(KEYS.USER_ID, id);
      safeSet(KEYS.SYNC_USER_PENDING, false);
    } else {
      localStorage.removeItem(KEYS.AUTHED_USER_ID);
    }
  },
  clearAuthedUserId: () => localStorage.removeItem(KEYS.AUTHED_USER_ID),

  /* Effective user id for API reads/writes.
   * - authed id, when the user is signed in
   * - the anonymous UUID otherwise (created on first call)
   * All Pro / suspect / user-scoped calls MUST use this helper. */
  getEffectiveUserId: () => {
    const authed = safeGet(KEYS.AUTHED_USER_ID, null);
    if (authed) return authed;
    // Same body as getOrCreateUserId — inline so we don't reference `this`.
    let id = safeGet(KEYS.USER_ID, null);
    if (!id) {
      id = genUuid();
      safeSet(KEYS.USER_ID, id);
      safeSet(KEYS.SYNC_USER_PENDING, true);
    }
    return id;
  },

  /* onboarding */
  isOnboardingComplete: () => safeGet(KEYS.ONBOARDING_COMPLETE, false) === true,
  setOnboardingComplete: () => safeSet(KEYS.ONBOARDING_COMPLETE, true),
  getSkinType: () => safeGet(KEYS.SKIN_TYPE, null),
  setSkinType: (val) => {
    safeSet(KEYS.SKIN_TYPE, val);
    safeSet(KEYS.SYNC_USER_PENDING, true);
  },

  /* scans */
  getScanCount: () => safeGet(KEYS.SCAN_COUNT, 0),
  incrementScanCount: () => {
    const next = safeGet(KEYS.SCAN_COUNT, 0) + 1;
    safeSet(KEYS.SCAN_COUNT, next);
    safeSet(KEYS.LAST_CHECKIN, new Date().toISOString());
    return next;
  },

  /* product cards on home / journal */
  getProducts: () => safeGet(KEYS.PRODUCTS, []),
  addProduct: (record) => {
    const list = safeGet(KEYS.PRODUCTS, []);
    list.unshift(record);
    safeSet(KEYS.PRODUCTS, list);
  },
  setProducts: (list) => safeSet(KEYS.PRODUCTS, list || []),

  getReactions: () => safeGet(KEYS.REACTIONS, []),
  addReaction: (record) => {
    const list = safeGet(KEYS.REACTIONS, []);
    list.unshift(record);
    safeSet(KEYS.REACTIONS, list);
  },
  setReactions: (list) => safeSet(KEYS.REACTIONS, list || []),

  getSafeList: () => safeGet(KEYS.SAFE_LIST, []),
  addToSafeList: (productName) => {
    const list = safeGet(KEYS.SAFE_LIST, []);
    if (!list.includes(productName)) {
      list.unshift(productName);
      safeSet(KEYS.SAFE_LIST, list);
    }
  },
  getDangerList: () => safeGet(KEYS.DANGER_LIST, []),
  addToDangerList: (productName) => {
    const list = safeGet(KEYS.DANGER_LIST, []);
    if (!list.includes(productName)) {
      list.unshift(productName);
      safeSet(KEYS.DANGER_LIST, list);
    }
  },

  /* product cache keyed by barcode */
  getCachedProductByBarcode: (barcode) => {
    const cache = safeGet(KEYS.PRODUCT_CACHE, {});
    return cache[barcode] || null;
  },
  setCachedProductByBarcode: (barcode, productRecord) => {
    const cache = safeGet(KEYS.PRODUCT_CACHE, {});
    cache[barcode] = productRecord;
    safeSet(KEYS.PRODUCT_CACHE, cache);
  },

  /* sync queues */
  queueScan: (scan) => {
    const q = safeGet(KEYS.SYNC_SCANS_PENDING, []);
    q.push(scan);
    safeSet(KEYS.SYNC_SCANS_PENDING, q);
  },
  drainScans: () => {
    const q = safeGet(KEYS.SYNC_SCANS_PENDING, []);
    safeSet(KEYS.SYNC_SCANS_PENDING, []);
    return q;
  },
  queueReaction: (r) => {
    const q = safeGet(KEYS.SYNC_REACTIONS_PENDING, []);
    q.push(r);
    safeSet(KEYS.SYNC_REACTIONS_PENDING, q);
  },
  drainReactions: () => {
    const q = safeGet(KEYS.SYNC_REACTIONS_PENDING, []);
    safeSet(KEYS.SYNC_REACTIONS_PENDING, []);
    return q;
  },

  /* server-synced caches (source of truth for unlock conditions) */
  getServerReactionCount: () => safeGet(KEYS.SERVER_REACTION_COUNT, 0),
  setServerReactionCount: (n) => safeSet(KEYS.SERVER_REACTION_COUNT, n),
  getSuspectCache: () => safeGet(KEYS.SUSPECT_CACHE, []),
  setSuspectCache: (list) => safeSet(KEYS.SUSPECT_CACHE, list || []),

  /* Pro state — localStorage is a cache; server /api/users/{id}.is_pro is truth */
  getIsPro: () => safeGet(KEYS.IS_PRO, false) === true,
  setIsPro: (val) => safeSet(KEYS.IS_PRO, !!val),

  /* last product returned by the backend (barcode / photo / manual) */
  getLastProduct: () => safeGet("sm_last_product", null),
  setLastProduct: (product) => safeSet("sm_last_product", product || null),

  /* Stats mirror (server-synced counters) */
  getCounters: () => safeGet("sm_counters", { scans: 0, reactions: 0, suspects: 0 }),
  setCounters: (obj) => safeSet("sm_counters", obj || {}),

  /* Auth — email persisted for account-switch detection */
  getEmail: () => safeGet("sm_email", null),
  setEmail: (email) => safeSet("sm_email", email || null),
  getFirstName: () => safeGet("sm_first_name", null),
  setFirstName: (v) => safeSet("sm_first_name", v || null),
  getManualTooltipShown: () => safeGet("sm_manual_tooltip_shown", false),
  setManualTooltipShown: () => safeSet("sm_manual_tooltip_shown", true),
  getLastName: () => safeGet("sm_last_name", null),
  setLastName: (v) => safeSet("sm_last_name", v || null),

  /* Skin check-ins — keeps last 10 thumbnails locally so the journal
   * feels instant. Full photo bytes stay on the server. */
  getSkinCheckins: () => safeGet("sm_skin_checkins", []),
  addSkinCheckin: (record) => {
    const list = safeGet("sm_skin_checkins", []);
    list.unshift(record);
    safeSet("sm_skin_checkins", list.slice(0, 10));
  },

  reset: () => Object.values(KEYS).forEach((k) => localStorage.removeItem(k)),
};

export default storage;
